import { Loader } from '@myosh/odin-components';
import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { apiBaseUrl, apiKey } from '../env';
import { getClusterConfig } from '../helpers/get-cluster-config';
import { useUser } from './user.context';

const fallbackClusterConfig: ClusterConfig = {
  apiBaseUrl,
  apiKey,
};

interface ClusterConfig {
  apiBaseUrl: string;
  apiKey: string;
}

interface ClusterContextProps {
  children: React.ReactNode;
}

const ClusterContext = createContext<ClusterConfig | undefined>(undefined);

export const ClusterProvider: React.FC<ClusterContextProps> = ({ children }) => {
  const [clusterConfigState, setClusterConfigState] = useState<ClusterConfig>();
  const { state: userState } = useUser();
  const schema = userState?.user?.currentSchema;
  const idToken = userState?.tokens?.id_token;

  useEffect(() => {
    const fetchConfig = () => {
      getClusterConfig(idToken ?? '', schema ?? '')
        .then((response) => {
          const { 'api-base-url': clusterApiBaseUrl, 'api-key': clusterApiKey } = response;
          const clusterConfig =
            clusterApiBaseUrl && clusterApiKey
              ? { apiBaseUrl: clusterApiBaseUrl, apiKey: clusterApiKey }
              : fallbackClusterConfig;
          localStorage.setItem('ClusterConfig', JSON.stringify(clusterConfig));
          setClusterConfigState(clusterConfig);
        })
        .catch(() => {
          localStorage.setItem('ClusterConfig', JSON.stringify(fallbackClusterConfig));
          setClusterConfigState(fallbackClusterConfig);
        });
    };

    fetchConfig();
  }, []);

  return (
    <ClusterContext.Provider value={clusterConfigState}>
      {clusterConfigState ? (
        children
      ) : (
        <div className="mx-auto flex h-screen w-full flex-col items-center justify-center">
          <Loader title="Loading configuration" />
        </div>
      )}
    </ClusterContext.Provider>
  );
};

// context consumer hook
export const useCluster = () => {
  const context = useContext(ClusterContext);

  if (context === undefined) {
    throw new Error('useCluster must be used within ClusterProvider');
  }

  return context;
};
